import React from 'react';
import useIsDesktop from 'tradera/hooks/use-is-desktop';
import {
    Trans,
    useCurrentLanguage,
    useTranslator
} from 'tradera/lang/translate';
import Phone from './phone.png';
import PhoneMd from './phone-2x.png';
import PhoneLg from './phone-3x.png';
import PhoneMobile from './phone-mobile.png';
import { Heading } from 'tradera/components/atoms/heading/heading';
import RatingStars from 'tradera/components/rating-stars/rating-stars';
import AppstoreLogo from '@tradera/blueprint/theme/logos/appstore.svg?next-image';
import GooglePlayLogo from '@tradera/blueprint/theme/logos/googleplay.svg?next-image';
import AppstoreBadgeEn from '@tradera/blueprint/theme/logos/app-store-badge-en.svg?jsx';
import AppstoreBadgeSe from '@tradera/blueprint/theme/logos/app-store-badge-se.svg?jsx';
import AppstoreBadgeDk from '@tradera/blueprint/theme/logos/app-store-badge-dk.svg?jsx';
import AppstoreBadgeDe from '@tradera/blueprint/theme/logos/app-store-badge-de.svg?jsx';
import AppstoreBadgeNl from '@tradera/blueprint/theme/logos/app-store-badge-nl.svg?jsx';
import AppstoreBadgeFr from '@tradera/blueprint/theme/logos/app-store-badge-fr.svg?jsx';
import GooglePlayBadgeEn from '@tradera/blueprint/theme/logos/google-play-badge-en.png?next-image';
import GooglePlayBadgeSe from '@tradera/blueprint/theme/logos/google-play-badge-se.png?next-image';
import GooglePlayBadgeDk from '@tradera/blueprint/theme/logos/google-play-badge-dk.png?next-image';
import GooglePlayBadgeDe from '@tradera/blueprint/theme/logos/google-play-badge-de.png?next-image';
import GooglePlayBadgeNl from '@tradera/blueprint/theme/logos/google-play-badge-nl.png?next-image';
import GooglePlayBadgeFr from '@tradera/blueprint/theme/logos/google-play-badge-fr.png?next-image';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { useAppDownloadLink } from 'tradera/hooks/use-app-download-link';
import styles from './app-download-banner.module.scss';
import { isNextJs } from 'tradera/utils/nextjs';

export const AppDownloadBanner = () => {
    const { t } = useTranslator();
    const { language } = useCurrentLanguage();
    const isDesktop = useIsDesktop();

    const trackClick = (store) => {
        GtmService.trackAction('StartPage', 'App download banner', store);
    };

    const { appStore, googlePlay } = useAppDownloadLink();

    return (
        <div className="bg-white">
            <div className={styles.appDownloadBanner}>
                <div className={`site-width ${styles.appDownloadWrapper}`}>
                    <div className={styles.appDownLoadBannerImage}>
                        {isDesktop ? (
                            <img
                                srcSet={`${Phone}, ${PhoneMd} 2x, ${PhoneLg} 3x`}
                                src={PhoneLg}
                                loading="lazy"
                            />
                        ) : (
                            <img src={PhoneMobile} loading="lazy" />
                        )}
                    </div>
                    <div
                        className={`${styles.appDownloadBannerContent} px-2 px-lg-4`}>
                        <Heading
                            as="h2"
                            size={isDesktop ? 'stockholm' : 'madrid'}
                            branded
                            className="mb-2 mt-2 mt-md-0">
                            <Trans
                                i18nKey="startPage_appDownloadBanner_text_Trans"
                                components={{
                                    branded: <span className="bg-branded" />
                                }}
                            />
                        </Heading>
                        <div className="d-flex mb-2">
                            <a
                                href={appStore}
                                rel="nofollow"
                                onClick={() => trackClick('App Store')}>
                                {isDesktop ? (
                                    <img
                                        src={AppstoreLogo}
                                        loading="lazy"
                                        height="28"
                                        title={t(
                                            'footer_downloadFromAppStore',
                                            {
                                                appName: 'App Store'
                                            }
                                        )}
                                        className="icon-dark mr-2"
                                    />
                                ) : (
                                    <AppstoreBadge language={language} />
                                )}
                            </a>
                            <a
                                href={googlePlay}
                                rel="nofollow"
                                onClick={() => trackClick('Google Play')}>
                                {isDesktop ? (
                                    <img
                                        src={GooglePlayLogo}
                                        loading="lazy"
                                        height="28"
                                        title={t(
                                            'footer_downloadFromAppStore',
                                            {
                                                appName: 'Google Play'
                                            }
                                        )}
                                        className="icon-dark"
                                    />
                                ) : (
                                    <GooglePlayBadge language={language} />
                                )}
                            </a>
                        </div>
                        <div>
                            <RatingStars rating={4.7} dark />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GooglePlayBadge = () => {
    const { language } = useCurrentLanguage();
    const { t } = useTranslator();

    let badge;

    switch (language) {
        case 'sv':
            badge = GooglePlayBadgeSe;
            break;
        case 'da':
            badge = GooglePlayBadgeDk;
            break;
        case 'de':
            badge = GooglePlayBadgeDe;
            break;
        case 'fr':
            badge = GooglePlayBadgeFr;
            break;
        case 'nl':
            badge = GooglePlayBadgeNl;
            break;
        default:
            badge = GooglePlayBadgeEn;
            break;
    }

    return (
        <img
            src={isNextJs ? badge.src : badge}
            loading="lazy"
            height="30"
            title={t('footer_downloadFromAppStore', {
                appName: 'Google Play'
            })}
        />
    );
};

const AppstoreBadge = ({ language }) => {
    switch (language) {
        case 'sv':
            return <AppstoreBadgeSe height="30" className="mr-2" />;
        case 'da':
            return <AppstoreBadgeDk height="30" className="mr-2" />;
        case 'de':
            return <AppstoreBadgeDe height="30" className="mr-2" />;
        case 'fr':
            return <AppstoreBadgeFr height="30" className="mr-2" />;
        case 'nl':
            return <AppstoreBadgeNl height="30" className="mr-2" />;
        default:
            return <AppstoreBadgeEn height="30" className="mr-2" />;
    }
};
